import React, { memo, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import {
  Divider, MenuItem, Select, Switch,
} from '@material-ui/core';
import { useMutation } from '@apollo/react-hooks';
import { useSnackbar } from 'notistack';
import CustomButton from '../../../components/CustomButton';
import { UPDATE_PROPERTY } from '../../../graphql/queries/PropertyDetails';
import { transformGraphQlErrorMessage } from '../../../utils/helpers';
import { Property } from '../../../types/Property';
import useStyles from "./styles";

const relockIntervalOptions = ['15 min', '20 min', '25 min', '30 min'];
const lockupOptions = [
  '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM',
  '10:00 PM', '10:30 PM', '11:00 PM',
];

type Props = {
  property: Property;
  propertyId: number;
} ;

const HubTab: React.FC<Props> = ({ property, propertyId}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [checked, setChecked] = useState(Boolean(property.access_settings) ?? false);
  const [relockInterval, setRelockInterval] = useState<string | null>(
    property.relock_interval ?? null
  );
  const [lockup, setLockup] = useState<string | null>(
    property.end_of_day_lockup ?? null
  );

  const [updateProperty] = useMutation<{ updateProperty: Property }>(UPDATE_PROPERTY);

  // Reset relock/lockup defaults when toggled
  useEffect(() => {
    if (!checked) {
      setRelockInterval(null);
      setLockup(null);
      return;
    }
    setRelockInterval(property.relock_interval ?? '15 min');
    setLockup(property.end_of_day_lockup ?? '10:00 PM');
  }, [checked, property.relock_interval, property.end_of_day_lockup]);

  const handleSave = () => {
    updateProperty({
      variables: {
        property: {
          property_id: propertyId,
          access_settings: checked,
          relock_interval: relockInterval,
          end_of_day_lockup: lockup,
        },
        communityId: property.propertyCommunity?.communities?.id ?? 0,
      },
    })
      .then(() => {
        enqueueSnackbar('Hub settings updated successfully', { variant: 'success' });
      })
      .catch(err => {
        enqueueSnackbar(transformGraphQlErrorMessage(err.message), { variant: 'error' });
      });
  };

  return (
    <Grid item xs={12} sm={12}>
      {/* We omit hub devices section (Admin doesn't show them) */}
      <Grid container alignItems="center" spacing={4}>
        <Grid item>
          <Switch
            checked={checked}
            onChange={() => setChecked(!checked)}
          />
        </Grid>

        <Grid item>
          <h2 className={`${classes.title} ${classes.primaryTitle}`}>
            Staffed Model Access Settings
          </h2>
        </Grid>
        <Grid item>
          <p
            className={`${classes.switchText} ${!checked && classes.disabled}`}
          >
            {checked ? 'Enabled' : 'Disabled'}
          </p>
        </Grid>
      </Grid>

      <Divider style={{ marginBottom: '20px' }} />

      <div className={`${!checked && classes.disabled}`}>
        {/* Relock Interval */}
        <Grid container alignItems="center">
          <Grid item>
            <p className={`${classes.title} ${classes.secondaryTitle}`}>
              Relock Interval
            </p>
          </Grid>
          <Grid item>
            <Select
              disabled={!checked}
              className={classes.select}
              variant="outlined"
              value={relockInterval ?? ''}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                setRelockInterval(event.target.value as string);
              }}
            >
              {relockIntervalOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        {/* End Of Day Lockup */}
        <Grid container alignItems="center">
          <Grid item>
            <p className={`${classes.title} ${classes.secondaryTitle}`}>
              End Of Day Lockup
            </p>
          </Grid>
          <Grid item>
            <Select
              disabled={!checked}
              className={classes.select}
              variant="outlined"
              value={lockup ?? ''}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                setLockup(event.target.value as string);
              }}
            >
              {lockupOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </div>

      <CustomButton
        onClick={handleSave}
        variant="orange"
        className={classes.btnStyle}
      >
        Save
      </CustomButton>
      <Divider style={{ marginTop: '20px' }} />
    </Grid>
  );
};

export default memo(HubTab);
